import React, { useState, useMemo, useEffect } from 'react';
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
} from 'recharts';
import {
  ShieldAlert, Activity, Info, ChevronDown, ChevronUp,
  Zap, Target, Clock, Globe, Sliders, Download,
  Send, AlertTriangle, TrendingDown, BarChart2,
  Network, FileText, Shield, Brain, Save, FlaskConical,
  RefreshCw, Copy, CheckCircle,
} from 'lucide-react';
import { API_BASE_URL as BASE_URL } from '../config';

// ─── Types ────────────────────────────────────────────────────────────────────
type SimMode = 'single' | 'ab' | 'batch';

interface TimePoint {
  time: string;
  negativeVolume: number;
  positiveVolume: number;
  baseline: number;
  viralityProjection: number;
  counterEffect?: number;
  label: string;
  signals: string;
}

interface SimData {
  timeline: TimePoint[];
  riskLabel: 'HIGH' | 'MEDIUM' | 'LOW';
  threatScore: number;
  peak: number;
  peakT: string;
  amplificationRisk: number;
  actorClusters: number;
  advice: string;
  riskFactors: string[];
  counterNarrative: string;
  criticalWindow: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────
const HIGH_RISK = ['election','vaccine','war','genocide','terror','bomb','protest','riot','coup','ban','lockdown','shutdown','censorship'];
const MED_RISK  = ['inflation','tax','climate','immigration','religion','police','drug','crime'];

const LOADING_PHASES = [
  'Parsing narrative structure…',
  'Running XGBoost classifier…',
  'OSINT cross-referencing 25 languages…',
  'Platform propagation curves…',
  'Counter-narrative modeling…',
  'Generating intelligence brief…',
];

const BATCH_SUFFIXES = [
  '',
  ' — citing official data',
  ' — framed as breaking news',
  ' — questioning authority',
  ' — emotional appeal',
  ' — statistical framing',
  ' — anonymous source',
  ' — celebrity endorsement',
  ' — viral question format',
  ' — counter-claim framing',
];

const GEO_OPTIONS = [
  { id: 'global',   label: 'Global' },
  { id: 'national', label: 'National' },
  { id: 'regional', label: 'Regional' },
  { id: 'custom',   label: 'Tamil Nadu' },
];

const ENV_OPTIONS = [
  { value: 'global_social', label: 'Global Web & Major Socials' },
  { value: 'fringe',        label: 'Fringe & Alt-Tech Platforms' },
  { value: 'finance',       label: 'Financial & Crypto Forums' },
  { value: 'dark_web',      label: 'Dark Web + Telegram Channels' },
];

const SIGNAL_POOL = [
  'Matches 3 shell domains from Intelligence Center',
  'Coordinated retweet pattern — 2 known clusters',
  'Cross-platform amplification signatures present',
  'Linked to 1 previously flagged campaign node',
  'OSINT: similar narrative spike logged T-48h',
  'No anomalous signals at this interval',
];

const SIM_MODES = [
  { id: 'single' as const, label: 'Single Run', Icon: Target },
  { id: 'ab'     as const, label: 'A/B Test',   Icon: FlaskConical },
  { id: 'batch'  as const, label: 'Batch 10',   Icon: BarChart2 },
];

// ─── Simulation engine ────────────────────────────────────────────────────────
function buildSimData(text: string): SimData {
  const lower = text.toLowerCase();
  const hits    = HIGH_RISK.filter(w => lower.includes(w)).length;
  const medHits = MED_RISK.filter(w => lower.includes(w)).length;
  const risk      = Math.min(0.95, 0.32 + hits * 0.14 + medHits * 0.07);
  const peak      = Math.round(100 + hits * 210 + medHits * 80 + Math.random() * 60);
  const threatScore      = Math.round(risk * 100);
  const peakHour         = hits >= 2 ? 12 : hits === 1 ? 18 : 30;
  const peakT            = `T+${peakHour}h`;
  const amplificationRisk = Math.min(95, Math.round(risk * 95 + 5));
  const actorClusters    = hits >= 2 ? 3 : hits === 1 ? 2 : 0;

  const timeline: TimePoint[] = [
    { time: 'T+0h',  negativeVolume: Math.round(60+risk*50),  positiveVolume: Math.round(90+(1-risk)*50),  baseline:100, viralityProjection:Math.round(50+risk*40),  label:'Content Published',        signals: SIGNAL_POOL[5] },
    { time: 'T+6h',  negativeVolume: Math.round(peak*0.28),   positiveVolume: Math.round(110+(1-risk)*55), baseline:100, viralityProjection:Math.round(peak*0.22),   label:'Early Amplification',      signals: SIGNAL_POOL[4] },
    { time: 'T+12h', negativeVolume: Math.round(peak*0.58),   positiveVolume: Math.round(130+(1-risk)*65), baseline:100, viralityProjection:Math.round(peak*0.52),   label:'Amplification Window',     signals: SIGNAL_POOL[1] },
    { time: 'T+18h', negativeVolume: Math.round(peak*0.84),   positiveVolume: Math.round(150+(1-risk)*75), baseline:100, viralityProjection:Math.round(peak*0.78),   label:'Critical Backlash Window', signals: SIGNAL_POOL[0] },
    { time: 'T+24h', negativeVolume: peak,                     positiveVolume: Math.round(165+(1-risk)*85), baseline:100, viralityProjection:peak,                    label:'Peak Backlash',            signals: SIGNAL_POOL[2] },
    { time: 'T+36h', negativeVolume: Math.round(peak*0.62),   positiveVolume: Math.round(195+(1-risk)*80), baseline:100, viralityProjection:Math.round(peak*0.48),   label:'Declining Intensity',      signals: SIGNAL_POOL[3] },
    { time: 'T+48h', negativeVolume: Math.round(peak*0.33),   positiveVolume: Math.round(225+(1-risk)*70), baseline:100, viralityProjection:Math.round(peak*0.22),   label:'Stabilisation',            signals: SIGNAL_POOL[5] },
  ];

  const riskLabel: 'HIGH' | 'MEDIUM' | 'LOW' = risk > 0.72 ? 'HIGH' : risk > 0.48 ? 'MEDIUM' : 'LOW';

  const riskFactors: string[] = [];
  if (hits >= 2)           riskFactors.push(`High-risk keyword density: ${hits} triggers detected`);
  else if (hits === 1)     riskFactors.push('1 high-risk trigger keyword detected');
  if (medHits >= 2)        riskFactors.push(`Moderate-risk signals: ${medHits} topics flagged`);
  if (risk > 0.6)          riskFactors.push('Coordinated inauthentic amplification likely within 12h');
  if (amplificationRisk > 50) riskFactors.push(`${actorClusters} known actor cluster(s) historically active on this narrative`);
  if (risk < 0.5)          riskFactors.push('Positive support projected to outpace backlash by T+24h');
  if (riskFactors.length === 0) riskFactors.push('No significant threat signals detected in current narrative pattern');

  const advice = risk > 0.72
    ? `High-probability coordinated amplification expected at ${peakT}. Issue a pre-emptive transparency statement 2h before peak. Activate counter-narrative seeding across X, Instagram, and Telegram simultaneously. Engage 3+ trusted amplifiers with factual framing.`
    : risk > 0.48
    ? `Moderate backlash risk detected. Monitor engagement velocity in the first 6h. Pre-draft clarification messaging. Engage 2-3 trusted amplifiers to seed positive framing before T+12h.`
    : `Low threat profile. Standard monitoring cadence is sufficient. Positive engagement is projected to outpace backlash by T+24h. Continue scheduled publishing.`;

  const counterNarrative = risk > 0.72
    ? `"[Brand/Entity] is committed to transparency and evidence-based action. Independent verification is available at [source]. We invite factual scrutiny and open dialogue."`
    : risk > 0.48
    ? `"This decision was reached in consultation with [stakeholder group] and is supported by [evidence source]. We welcome community dialogue and factual engagement."`
    : `"Our position is informed by [data/research] and reflects our commitment to [value]. Learn more at [link]. We are open to questions."`;

  const criticalWindow = risk > 0.72
    ? `CRITICAL BACKLASH WINDOW ${peakT}`
    : risk > 0.48
    ? `ELEVATED RISK WINDOW ${peakT}`
    : 'LOW BOYCOTT RISK';

  return { timeline, riskLabel, threatScore, peak, peakT, amplificationRisk, actorClusters, advice, riskFactors, counterNarrative, criticalWindow };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtK(n: number): string {
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
}

function buildSparkData(peak: number): Array<{ v: number }> {
  return [0.08, 0.22, 0.45, 0.72, 1.0, 0.78, 0.42].map(f => ({ v: Math.round(peak * f) }));
}

function riskColor(label: string): { text: string; bg: string; border: string } {
  if (label === 'HIGH')   return { text: 'text-rose-400',    bg: 'bg-rose-500/10',    border: 'border-rose-500/25' };
  if (label === 'MEDIUM') return { text: 'text-amber-400',   bg: 'bg-amber-500/10',   border: 'border-amber-500/25' };
  return                         { text: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/25' };
}

// ─── Main Component ────────────────────────────────────────────────────────────
export const CampaignSimulation: React.FC = () => {
  const [policyText, setPolicyText]                     = useState('');
  const [abTextB, setAbTextB]                           = useState('');
  const [simMode, setSimMode]                           = useState<SimMode>('single');
  const [isSimulating, setIsSimulating]                 = useState(false);
  const [loadingPct, setLoadingPct]                     = useState(0);
  const [loadingPhase, setLoadingPhase]                 = useState('');
  const [simResult, setSimResult]                       = useState<SimData | null>(null);
  const [abResult, setAbResult]                         = useState<SimData | null>(null);
  const [batchResults, setBatchResults]                 = useState<Array<{ text: string; risk: string; score: number }> | null>(null);
  const [howItWorksOpen, setHowItWorksOpen]             = useState(false);
  const [geoTargets, setGeoTargets]                     = useState<string[]>(['global']);
  const [envTarget, setEnvTarget]                       = useState('global_social');
  const [amplifierBoost, setAmplifierBoost]             = useState(0);
  const [timingHour, setTimingHour]                     = useState(9);
  const [platformMix, setPlatformMix]                   = useState('all');
  const [influencerWhatIf, setInfluencerWhatIf]         = useState(0);
  const [counterNarrativeActive, setCounterNarrativeActive] = useState(false);
  const [copied, setCopied]                             = useState(false);
  const [simHistory, setSimHistory]                     = useState<Array<{ ts: string; text: string; score: number; label: 'HIGH'|'MEDIUM'|'LOW'; peak: number }>>([]);

  // BI Phase 4 integration: strategic scenarios & risk forecasts
  const [biScenarios, setBiScenarios] = useState<{ id: number; scenario_name: string; scenario_type: string; probability: number; impact_level: string; description: string }[]>([]);
  const [biForecast, setBiForecast]   = useState<{ id: number; risk_level: string; predicted_score: number; forecast_horizon: string; key_drivers: string[] } | null>(null);
  const [biSimLoading, setBiSimLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      setBiSimLoading(true);
      try {
        const [scRes, fcRes] = await Promise.all([
          fetch(`${BASE_URL}/api/bi/predictive/scenario/Technology`),
          fetch(`${BASE_URL}/api/bi/predictive/forecast/Technology`),
        ]);
        if (scRes.ok) { const d = await scRes.json(); setBiScenarios(Array.isArray(d) ? d.slice(0, 4) : []); }
        if (fcRes.ok) { const d = await fcRes.json(); setBiForecast(Array.isArray(d) ? d[0] ?? null : d ?? null); }
      } catch { /* non-blocking */ } finally { setBiSimLoading(false); }
    };
    load();
  }, []);

  const toggleGeo = (geo: string) => {
    setGeoTargets(prev =>
      prev.includes(geo)
        ? prev.length > 1 ? prev.filter(g => g !== geo) : prev
        : [...prev, geo]
    );
  };

  const runAnimation = (onDone: () => void) => {
    let step = 0;
    const TOTAL = 60;
    const iv = setInterval(() => {
      step += 1;
      setLoadingPct(Math.round((step / TOTAL) * 100));
      setLoadingPhase(LOADING_PHASES[Math.min(Math.floor((step / TOTAL) * LOADING_PHASES.length), LOADING_PHASES.length - 1)]);
      if (step >= TOTAL) { clearInterval(iv); onDone(); }
    }, 42);
  };

  const handleSimulate = async () => {
    if (!policyText.trim()) return;
    setIsSimulating(true);
    setSimResult(null);
    setAbResult(null);
    setBatchResults(null);
    setLoadingPct(0);

    const localA = buildSimData(policyText);
    const localB = simMode === 'ab' && abTextB.trim() ? buildSimData(abTextB) : null;

    const pushHistory = (result: SimData) => {
      setSimHistory(prev => [
        { ts: new Date().toLocaleTimeString(), text: policyText.slice(0, 60), score: result.threatScore, label: result.riskLabel, peak: result.peak },
        ...prev.slice(0, 4),
      ]);
    };

    try {
      const res = await fetch(`${BASE_URL}/api/v1/simulation/predict`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: policyText }),
      });
      if (res.ok) {
        const data = await res.json();
        const pred = data.prediction || {};
        const apiRisk = (pred.risk_level || '').toUpperCase();
        const label = (['HIGH', 'MEDIUM', 'LOW'] as const).find(r => r === apiRisk) ?? localA.riskLabel;
        const merged = { ...localA, riskLabel: label };
        setSimResult(merged);
        pushHistory(merged);
      } else {
        setSimResult(localA);
        pushHistory(localA);
      }
    } catch {
      setSimResult(localA);
      pushHistory(localA);
    }

    if (localB) setAbResult(localB);
    runAnimation(() => setIsSimulating(false));
  };

  const handleQuickTest = () => {
    if (!policyText.trim()) return;
    setSimResult(buildSimData(policyText));
    setAbResult(null);
    setBatchResults(null);
  };

  const handleBatch = () => {
    if (!policyText.trim()) return;
    const results = BATCH_SUFFIXES.map((suffix, i) => {
      const d = buildSimData(policyText + suffix);
      const snippet = (policyText + suffix).slice(0, 55);
      return { text: `V${String.fromCharCode(65 + i)}: ${snippet}…`, risk: d.riskLabel, score: d.threatScore };
    });
    setBatchResults(results);
    setSimResult(null);
  };

  const handleExportJSON = () => {
    if (!simResult) return;
    const payload = {
      simulation: simResult,
      parameters: { amplifierBoost, timingHour, platformMix, geoTargets, envTarget },
      timestamp: new Date().toISOString(),
      model: 'XGBoost + LLM Ensemble v2',
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `crisis-simulation-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCopy = () => {
    if (!simResult?.counterNarrative) return;
    navigator.clipboard.writeText(simResult.counterNarrative);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // What-If: real-time chart recalc
  const chartData = useMemo(() => {
    if (!simResult) return [];
    const boost = 1 + influencerWhatIf / 100;
    return simResult.timeline.map(pt => ({
      ...pt,
      negativeVolume:     Math.round(pt.negativeVolume * boost),
      positiveVolume:     Math.round(pt.positiveVolume * boost),
      viralityProjection: Math.round(pt.viralityProjection * boost),
      ...(counterNarrativeActive ? { counterEffect: Math.round(pt.negativeVolume * boost * 0.52) } : {}),
    }));
  }, [simResult, influencerWhatIf, counterNarrativeActive]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="w-full pb-12 space-y-5">

      {/* TOP BAR */}
      <div className="flex items-center justify-between bg-zinc-900/60 border border-zinc-800/40 rounded-xl px-4 py-2 text-xs">
        <div className="flex items-center gap-4 text-zinc-400 flex-wrap">
          <span className="flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5 text-indigo-400" />
            Last Model Trained: <span className="text-zinc-300 font-medium ml-1">23 min ago</span>
          </span>
          <span>•</span>
          <span>Accuracy: <span className="text-emerald-400 font-semibold">94%</span></span>
          <span>•</span>
          <span className="flex items-center gap-1">
            <Globe className="w-3.5 h-3.5" /> 25 languages active
          </span>
          <span className="hidden md:inline">•</span>
          <span className="hidden md:inline">X · Instagram · TikTok · Telegram · Dark Web</span>
        </div>
        <button className="flex items-center gap-1.5 text-indigo-400 hover:text-indigo-300 transition-colors shrink-0">
          <RefreshCw className="w-3.5 h-3.5" /> Request Retrain
        </button>
      </div>

      {/* HEADER */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-white mb-1">Executive Simulator Studio</h1>
        <p className="text-zinc-400 text-sm leading-relaxed max-w-3xl">
          Multi-Model Narrative Forecaster — Predict spread, backlash &amp; amplification risk before publishing.<br />
          <span className="text-indigo-400">Powered by XGBoost + LLM Ensemble + OSINT signals.</span>
        </p>
      </div>

      {/* BI STRATEGIC SCENARIOS PANEL — Phase 4 Simulation Engine Integration */}
      {(biScenarios.length > 0 || biForecast) && (
        <div className="bg-indigo-500/5 border border-indigo-500/20 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Brain className="w-4 h-4 text-indigo-400" />
              <span className="text-sm font-bold text-white">BI Strategic Scenarios</span>
              <span className="text-xs text-zinc-400">Phase 4 predictive intelligence · Technology sector</span>
            </div>
            {biForecast && (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-zinc-400">Forecast Risk:</span>
                <span className={`font-black font-mono ${
                  biForecast.risk_level === 'CRITICAL' ? 'text-rose-400' :
                  biForecast.risk_level === 'HIGH'     ? 'text-amber-400' :
                  biForecast.risk_level === 'MEDIUM'   ? 'text-yellow-400' : 'text-emerald-400'
                }`}>{biForecast.predicted_score?.toFixed(0)}/100 · {biForecast.risk_level}</span>
                {biForecast.forecast_horizon && <span className="text-zinc-400">{biForecast.forecast_horizon}</span>}
              </div>
            )}
            {biSimLoading && <RefreshCw className="w-3.5 h-3.5 text-indigo-400 animate-spin" />}
          </div>

          {biScenarios.length > 0 && (
            <>
              <p className="text-xs text-zinc-400 mb-2">Click a scenario to pre-fill the simulator:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {biScenarios.map(s => {
                  const impColor = s.impact_level === 'CRITICAL' ? 'border-rose-500/30 bg-rose-500/5 text-rose-300' :
                                   s.impact_level === 'HIGH'     ? 'border-amber-500/30 bg-amber-500/5 text-amber-300' :
                                                                    'border-indigo-500/20 bg-indigo-500/5 text-indigo-300';
                  return (
                    <button
                      key={s.id}
                      onClick={() => { if (s.description) setPolicyText(s.description.slice(0, 280)); }}
                      className={`text-left rounded-xl border px-3 py-2.5 transition-all hover:scale-[1.01] ${impColor}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold uppercase">{s.impact_level}</span>
                        <span className="text-xs text-zinc-400">{(s.probability * 100).toFixed(0)}% prob</span>
                        <span className="text-xs text-zinc-400 ml-auto">{s.scenario_type}</span>
                      </div>
                      <p className="text-xs text-zinc-200 font-medium truncate">{s.scenario_name}</p>
                      {s.description && <p className="text-xs text-zinc-400 mt-0.5 truncate">{s.description.slice(0, 80)}…</p>}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {biForecast?.key_drivers && biForecast.key_drivers.length > 0 && (
            <div className="mt-3 pt-3 border-t border-indigo-500/15">
              <p className="text-xs text-zinc-400 mb-2">Key Risk Drivers:</p>
              <div className="flex flex-wrap gap-1.5">
                {biForecast.key_drivers.slice(0, 5).map((d, i) => (
                  <span key={i} className="text-xs px-2 py-0.5 rounded-lg bg-zinc-800 border border-zinc-700 text-zinc-300">{d}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* HOW IT WORKS — expandable */}
      <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-xl overflow-hidden">
        <button
          onClick={() => setHowItWorksOpen(v => !v)}
          className="w-full flex items-center gap-3 px-4 py-3 text-sm text-indigo-300 hover:text-indigo-200 transition-colors"
        >
          <Info className="w-4 h-4 shrink-0" />
          <span className="font-medium">How it works — Live Model Transparency</span>
          <span className="ml-auto">
            {howItWorksOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </span>
        </button>
        {howItWorksOpen && (
          <div className="px-5 pb-5 border-t border-indigo-500/20">
            <p className="text-sm text-zinc-400 leading-relaxed mt-4 mb-3">
              Enter any proposed statement, policy, or headline. The engine applies a multi-layer intelligence stack and returns a 48h projected fallout map.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Languages',  value: '25+',  sub: 'Real-time NLP'             },
                { label: 'Platforms',  value: '6+',   sub: 'X, IG, TT, TG, Dark Web'  },
                { label: 'Horizon',    value: '48h',  sub: 'Propagation model'         },
                { label: 'Accuracy',   value: '93%',  sub: 'Historical validation'     },
              ].map(s => (
                <div key={s.label} className="bg-zinc-900/60 rounded-xl p-3 text-center">
                  <p className="text-xl font-bold text-indigo-300">{s.value}</p>
                  <p className="text-xs font-semibold text-white">{s.label}</p>
                  <p className="text-xs text-zinc-400 mt-0.5">{s.sub}</p>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-zinc-400 mt-3">
              Negative Backlash = organised rejection &amp; boycott activity · Positive Support = counter-voices &amp; endorsers · Virality Curve = projected dashed amplification arc
            </p>
          </div>
        )}
      </div>

      {/* SIM MODE TABS */}
      <div className="flex gap-2 flex-wrap">
        {SIM_MODES.map(({ id, label, Icon }) => (
          <button
            key={id}
            onClick={() => setSimMode(id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-all border ${
              simMode === id
                ? 'bg-indigo-500/20 border-indigo-500/40 text-indigo-300'
                : 'bg-zinc-900/40 border-zinc-800/50 text-zinc-400 hover:text-zinc-200'
            }`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {/* INPUT PANEL */}
      <div className="bg-zinc-900/40 backdrop-blur-xl border border-zinc-800/50 rounded-2xl p-6 shadow-xl">
        <h3 className="text-base font-bold text-white mb-4 flex items-center gap-2">
          <Activity className="w-5 h-5 text-cyan-400" /> Advanced Scenario Builder
        </h3>

        <div className="space-y-5">

          {/* Text area(s) */}
          {simMode !== 'ab' ? (
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1.5">Narrative / Policy / Headline</label>
              <textarea
                className="w-full bg-zinc-950/50 border border-zinc-800 rounded-xl p-3 text-zinc-200 placeholder-zinc-600 focus:border-cyan-500 focus:ring focus:ring-cyan-500/15 outline-none transition-all resize-none text-sm"
                rows={4}
                value={policyText}
                onChange={e => setPolicyText(e.target.value)}
                placeholder="Enter any proposed statement, policy, thread, or news headline…"
              />
              <div className="flex gap-2 mt-2 flex-wrap">
                {[
                  { chip: 'Load from Narrative Mapping', text: '' },
                  { chip: '🗳 Election claim', text: 'Breaking: Election officials confirm irregularities in vote counting across 3 districts' },
                  { chip: '💊 Health scare',   text: 'Leaked report: Vaccine batch linked to 47 unexplained adverse reactions in southern states' },
                  { chip: '📦 Supply-chain',   text: 'Supply chain crisis worsens — government hiding true shortage figures from public' },
                ].map(({ chip, text }) => (
                  <button
                    key={chip}
                    onClick={() => { if (text) setPolicyText(text); }}
                    className="px-2.5 py-1 text-xs bg-zinc-800/60 hover:bg-zinc-700/60 border border-zinc-700/50 text-zinc-300 rounded-lg transition-colors"
                  >
                    {chip}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1.5">Variant A — Original</label>
                <textarea
                  className="w-full bg-zinc-950/50 border border-zinc-800 rounded-xl p-3 text-zinc-200 placeholder-zinc-600 focus:border-cyan-500 focus:ring focus:ring-cyan-500/15 outline-none transition-all resize-none text-sm"
                  rows={5}
                  value={policyText}
                  onChange={e => setPolicyText(e.target.value)}
                  placeholder="Variant A — original narrative…"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1.5">Variant B — Modified Framing</label>
                <textarea
                  className="w-full bg-zinc-950/50 border border-zinc-800 rounded-xl p-3 text-zinc-200 placeholder-zinc-600 focus:border-violet-500 focus:ring focus:ring-violet-500/15 outline-none transition-all resize-none text-sm"
                  rows={5}
                  value={abTextB}
                  onChange={e => setAbTextB(e.target.value)}
                  placeholder="Variant B — reframed or modified version…"
                />
              </div>
            </div>
          )}

          {/* Sliders */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div>
              <label className="flex items-center gap-1.5 text-xs font-medium text-zinc-400 mb-2">
                <Zap className="w-3.5 h-3.5 text-amber-400" />
                Amplifier Boost: <span className="text-amber-400 font-bold ml-1">{amplifierBoost}x influencer seed</span>
              </label>
              <input type="range" min={0} max={10} step={1} value={amplifierBoost}
                onChange={e => setAmplifierBoost(Number(e.target.value))}
                className="w-full h-1.5 bg-zinc-700 rounded-full appearance-none cursor-pointer accent-amber-500" />
              <div className="flex justify-between text-xs text-zinc-400 mt-1"><span>None</span><span>10x</span></div>
            </div>
            <div>
              <label className="flex items-center gap-1.5 text-xs font-medium text-zinc-400 mb-2">
                <Clock className="w-3.5 h-3.5 text-indigo-400" />
                Publish Timing: <span className="text-indigo-400 font-bold ml-1">{String(timingHour).padStart(2,'0')}:00</span>
              </label>
              <input type="range" min={0} max={23} step={1} value={timingHour}
                onChange={e => setTimingHour(Number(e.target.value))}
                className="w-full h-1.5 bg-zinc-700 rounded-full appearance-none cursor-pointer accent-indigo-500" />
              <div className="flex justify-between text-xs text-zinc-400 mt-1"><span>00:00</span><span>23:00</span></div>
            </div>
            <div>
              <label className="flex items-center gap-1.5 text-xs font-medium text-zinc-400 mb-2">
                <Globe className="w-3.5 h-3.5 text-cyan-400" /> Platform Mix
              </label>
              <select value={platformMix} onChange={e => setPlatformMix(e.target.value)}
                className="w-full bg-zinc-950/50 border border-zinc-800 rounded-xl px-3 py-2 text-sm text-zinc-200 focus:border-cyan-500 outline-none">
                {['all','x','instagram','tiktok','telegram'].map(p => (
                  <option key={p} value={p} className="bg-zinc-900">
                    {p === 'all' ? 'All Platforms' : p.charAt(0).toUpperCase() + p.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Geo chips + Environment */}
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs text-zinc-400 font-medium flex items-center gap-1 mr-1">
              <Globe className="w-3.5 h-3.5" /> Target Geo:
            </span>
            {GEO_OPTIONS.map(g => (
              <button
                key={g.id}
                onClick={() => toggleGeo(g.id)}
                className={`px-3 py-1 rounded-lg text-xs font-medium border transition-all ${
                  geoTargets.includes(g.id)
                    ? 'bg-cyan-500/20 border-cyan-500/40 text-cyan-300'
                    : 'bg-zinc-800/50 border-zinc-700/40 text-zinc-400 hover:text-zinc-200'
                }`}
              >
                {g.label}
              </button>
            ))}
            <span className="text-zinc-700 mx-1">|</span>
            <select value={envTarget} onChange={e => setEnvTarget(e.target.value)}
              className="bg-zinc-900/50 border border-zinc-700/40 rounded-lg px-3 py-1 text-xs text-zinc-300 focus:border-cyan-500 outline-none">
              {ENV_OPTIONS.map(o => (
                <option key={o.value} value={o.value} className="bg-zinc-900">{o.label}</option>
              ))}
            </select>
          </div>

          {/* Button row */}
          <div className="flex gap-3 flex-wrap pt-1">
            <button
              onClick={handleSimulate}
              disabled={isSimulating || !policyText.trim()}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-white bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-cyan-500/20 transition-all text-sm"
            >
              <Target className="w-4 h-4" /> Run Full Simulation
            </button>
            <button
              onClick={handleQuickTest}
              disabled={!policyText.trim()}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-zinc-200 bg-zinc-800/60 hover:bg-zinc-700/60 border border-zinc-700/50 disabled:opacity-40 text-sm transition-colors"
            >
              <Zap className="w-4 h-4 text-amber-400" /> Quick Test
            </button>
            <button
              onClick={handleBatch}
              disabled={!policyText.trim()}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-zinc-200 bg-zinc-800/60 hover:bg-zinc-700/60 border border-zinc-700/50 disabled:opacity-40 text-sm transition-colors"
            >
              <BarChart2 className="w-4 h-4 text-violet-400" /> Batch 10 Headlines
            </button>
          </div>
        </div>
      </div>

      {/* LOADING BAR */}
      {isSimulating && (
        <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-2xl p-5">
          <div className="flex justify-between text-xs text-zinc-400 mb-2">
            <span className="font-medium text-indigo-300">{loadingPhase}</span>
            <span className="font-mono text-white">{loadingPct}%</span>
          </div>
          <div className="w-full bg-zinc-800/50 rounded-full h-2 overflow-hidden">
            <div
              className="h-2 rounded-full bg-gradient-to-r from-cyan-500 via-indigo-500 to-violet-500 transition-all duration-100"
              style={{ width: `${loadingPct}%` }}
            />
          </div>
        </div>
      )}

      {/* BATCH RESULTS */}
      {batchResults && !isSimulating && (
        <div className="bg-zinc-900/40 backdrop-blur-xl border border-zinc-800/50 rounded-2xl p-6 shadow-xl">
          <h3 className="text-base font-bold text-white mb-4 flex items-center gap-2">
            <BarChart2 className="w-5 h-5 text-violet-400" /> Batch Simulation — 10 Headline Variants
          </h3>
          <div className="space-y-2">
            {batchResults.map((r, i) => (
              <div key={i} className="flex items-center gap-3 bg-zinc-800/30 rounded-xl px-4 py-2.5">
                <span className={`text-xs font-bold px-2 py-0.5 rounded-lg ${
                  r.risk === 'HIGH'   ? 'bg-rose-500/20 text-rose-400' :
                  r.risk === 'MEDIUM' ? 'bg-amber-500/20 text-amber-400' :
                                        'bg-emerald-500/20 text-emerald-400'
                }`}>{r.risk}</span>
                <span className="text-zinc-400 text-xs flex-1 truncate">{r.text}</span>
                <div className="flex items-center gap-2 shrink-0">
                  <div className="w-20 bg-zinc-700/50 rounded-full h-1.5">
                    <div className={`h-1.5 rounded-full ${r.score > 70 ? 'bg-rose-500' : r.score > 45 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                      style={{ width: `${r.score}%` }} />
                  </div>
                  <span className={`text-xs font-mono font-bold w-6 text-right ${r.score > 70 ? 'text-rose-400' : r.score > 45 ? 'text-amber-400' : 'text-emerald-400'}`}>{r.score}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* FULL RESULTS */}
      {simResult && !isSimulating && (() => {
        const rc = riskColor(simResult.riskLabel);
        const sparkData = buildSparkData(simResult.peak);
        return (
          <div className="space-y-5">

            {/* Badge + Save */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <span className={`px-4 py-2 rounded-xl font-bold text-sm border flex items-center gap-2 ${rc.bg} ${rc.border} ${rc.text}`}>
                <ShieldAlert className="w-4 h-4" /> {simResult.criticalWindow}
              </span>
              <button onClick={handleExportJSON}
                className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-zinc-200 transition-colors border border-zinc-700/50 rounded-xl px-3 py-2">
                <Save className="w-3.5 h-3.5" /> Save Simulation Scenario
              </button>
            </div>

            {/* WHAT-IF CONTROLS */}
            <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-2xl p-5 shadow-xl">
              <h4 className="text-sm font-bold text-zinc-300 mb-4 flex items-center gap-2">
                <Sliders className="w-4 h-4 text-violet-400" /> What-If Controls — Real-time Chart Adjustment
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-xs text-zinc-400 mb-2 block">
                    Influencer Reach Boost: <span className="text-violet-400 font-bold">+{influencerWhatIf}%</span>
                  </label>
                  <input type="range" min={0} max={100} step={5} value={influencerWhatIf}
                    onChange={e => setInfluencerWhatIf(Number(e.target.value))}
                    className="w-full h-1.5 bg-zinc-700 rounded-full appearance-none cursor-pointer accent-violet-500" />
                  <div className="flex justify-between text-xs text-zinc-400 mt-1"><span>No boost</span><span>+100%</span></div>
                </div>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setCounterNarrativeActive(v => !v)}
                    className={`relative inline-flex h-5 w-10 shrink-0 cursor-pointer items-center rounded-full border-2 transition-colors ${
                      counterNarrativeActive ? 'border-emerald-500 bg-emerald-500' : 'border-zinc-600 bg-zinc-700'
                    }`}
                  >
                    <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${
                      counterNarrativeActive ? 'translate-x-5' : 'translate-x-0.5'
                    }`} />
                  </button>
                  <div>
                    <p className="text-xs font-medium text-zinc-300">Add Counter-Narrative Overlay</p>
                    <p className="text-xs text-zinc-400">Suppressed backlash line if pre-bunking deployed at T+0h</p>
                  </div>
                </div>
              </div>
            </div>

            {/* 48H BATTLEFIELD CHART */}
            <div className="bg-zinc-900/40 backdrop-blur-xl border border-zinc-800/50 rounded-2xl p-6 shadow-xl">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-5 gap-3">
                <div>
                  <h3 className="text-base font-bold text-white">Projected Fallout — 48h Battlefield</h3>
                  <p className="text-xs text-zinc-400 mt-0.5">Hover any point for OSINT signal annotation · Red gradient = backlash severity</p>
                </div>
                {abResult && (
                  <span className="text-xs text-zinc-400 bg-zinc-800/60 border border-zinc-700/40 rounded-lg px-3 py-1.5">
                    A/B: Variant A chart · Variant B compared below
                  </span>
                )}
              </div>
              <div className="h-80 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="negGrad2" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#f43f5e" stopOpacity={0.45} />
                        <stop offset="95%" stopColor="#f43f5e" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="posGrad2" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#10b981" stopOpacity={0.35} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="virGrad2" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#818cf8" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="#818cf8" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
                    <XAxis dataKey="time" stroke="#52525b" fontSize={11} tickMargin={10} />
                    <YAxis stroke="#52525b" fontSize={11} />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (!active || !payload?.length || !label) return null;
                        const pt = simResult.timeline.find(d => d.time === label);
                        return (
                          <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-3 text-xs shadow-2xl max-w-xs">
                            <p className="font-bold text-white mb-1">{label}{pt?.label ? ` — ${pt.label}` : ''}</p>
                            {(payload as Array<{ name: string; value: number; color: string }>).map((p, i) => (
                              <p key={i} style={{ color: p.color }} className="mb-0.5">
                                {p.name}: <span className="font-mono font-bold">{p.value.toLocaleString()}</span> posts/hr
                              </p>
                            ))}
                            {pt?.signals && (
                              <p className="mt-2 pt-2 border-t border-zinc-700 text-zinc-400 italic">{pt.signals}</p>
                            )}
                          </div>
                        );
                      }}
                    />
                    <Legend wrapperStyle={{ paddingTop: '14px', fontSize: '11px' }} />
                    <ReferenceLine x={simResult.peakT} stroke="#f43f5e" strokeDasharray="4 4" strokeWidth={1.5}
                      label={{ value: 'PEAK', fill: '#f43f5e', fontSize: 9 }} />
                    <Area type="monotone" dataKey="negativeVolume"    stroke="#f43f5e" strokeWidth={2.5} fillOpacity={1} fill="url(#negGrad2)" name="Negative Backlash (posts/hr)" />
                    <Area type="monotone" dataKey="positiveVolume"    stroke="#10b981" strokeWidth={2.5} fillOpacity={1} fill="url(#posGrad2)" name="Positive Support (posts/hr)" />
                    <Area type="monotone" dataKey="baseline"          stroke="#52525b" strokeDasharray="5 5" fill="none" strokeWidth={1.5} name="Normal Baseline" />
                    <Area type="monotone" dataKey="viralityProjection" stroke="#818cf8" strokeDasharray="6 3" strokeWidth={2} fillOpacity={1} fill="url(#virGrad2)" name="Projected Virality Curve" />
                    {counterNarrativeActive && (
                      <Area type="monotone" dataKey="counterEffect" stroke="#34d399" strokeDasharray="3 3" strokeWidth={2} fill="none" name="Counter-Narrative Suppression" />
                    )}
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* A/B COMPARISON */}
            {abResult && (
              <div className="grid grid-cols-2 gap-4">
                {[{ label: 'Variant A', data: simResult }, { label: 'Variant B', data: abResult }].map(({ label, data }) => {
                  const arc = riskColor(data.riskLabel);
                  return (
                    <div key={label} className={`bg-zinc-900/40 border rounded-2xl p-4 ${arc.border}`}>
                      <p className="text-xs font-bold text-zinc-400 mb-2">{label}</p>
                      <p className={`text-2xl font-bold font-mono ${arc.text}`}>
                        {data.threatScore}<span className="text-sm text-zinc-400 font-normal">/100</span>
                      </p>
                      <p className={`text-xs font-bold mt-1 ${arc.text}`}>{data.riskLabel} RISK</p>
                      <p className="text-xs text-zinc-400 mt-1">Peak: {fmtK(data.peak)} posts/hr @ {data.peakT}</p>
                    </div>
                  );
                })}
              </div>
            )}

            {/* STAT CARDS */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Peak Backlash */}
              <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-2xl p-5 shadow-xl">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-medium text-zinc-400 flex items-center gap-1.5">
                    <TrendingDown className="w-3.5 h-3.5 text-rose-400" /> Peak Backlash
                  </p>
                  <span className="text-xs text-rose-400 font-semibold">CRITICAL</span>
                </div>
                <p className="text-2xl font-bold text-rose-400 font-mono">
                  {fmtK(simResult.peak)}<span className="text-sm text-zinc-400 font-normal"> posts/hr</span>
                </p>
                <p className="text-xs text-zinc-400 mt-0.5">@ {simResult.peakT}</p>
                <div className="h-10 mt-3 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={sparkData}>
                      <Line type="monotone" dataKey="v" stroke="#f43f5e" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Amplification Risk */}
              <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-2xl p-5 shadow-xl">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-medium text-zinc-400 flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-400" /> Amplification Risk
                  </p>
                  {simResult.actorClusters > 0 && (
                    <span className="text-xs text-amber-400 font-semibold">{simResult.actorClusters} CLUSTERS</span>
                  )}
                </div>
                <p className={`text-2xl font-bold font-mono ${simResult.amplificationRisk > 60 ? 'text-rose-400' : simResult.amplificationRisk > 35 ? 'text-amber-400' : 'text-emerald-400'}`}>
                  {simResult.amplificationRisk}%
                </p>
                <p className="text-xs text-zinc-400 mt-0.5">
                  {simResult.actorClusters > 0
                    ? `linked to ${simResult.actorClusters} known actor cluster${simResult.actorClusters > 1 ? 's' : ''}`
                    : 'no known actor clusters detected'}
                </p>
                <div className="mt-3 w-full bg-zinc-800/50 rounded-full h-2">
                  <div className={`h-2 rounded-full transition-all ${simResult.amplificationRisk > 60 ? 'bg-rose-500' : simResult.amplificationRisk > 35 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                    style={{ width: `${simResult.amplificationRisk}%` }} />
                </div>
              </div>

              {/* Threat Score */}
              <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-2xl p-5 shadow-xl">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-medium text-zinc-400 flex items-center gap-1.5">
                    <Shield className="w-3.5 h-3.5 text-indigo-400" /> Overall Threat Score
                  </p>
                  <span className={`text-xs font-bold ${rc.text}`}>{simResult.riskLabel}</span>
                </div>
                <p className={`text-2xl font-bold font-mono ${rc.text}`}>
                  {simResult.threatScore}<span className="text-sm text-zinc-400 font-normal">/100</span>
                </p>
                <p className="text-xs text-zinc-400 mt-0.5">XGBoost + LLM ensemble</p>
                <div className="mt-3 w-full bg-zinc-800/50 rounded-full h-2">
                  <div className={`h-2 rounded-full transition-all ${simResult.threatScore > 70 ? 'bg-rose-500' : simResult.threatScore > 45 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                    style={{ width: `${simResult.threatScore}%` }} />
                </div>
              </div>
            </div>

            {/* AI INTELLIGENCE DECK */}
            <div className={`bg-zinc-900/40 backdrop-blur-xl border rounded-2xl p-6 shadow-xl ${rc.border}`}>
              <h4 className={`font-bold text-base mb-5 flex items-center gap-2 ${rc.text}`}>
                <Brain className="w-5 h-5" /> AI Strategic Intelligence Deck
              </h4>

              {/* Risk breakdown */}
              <div className="mb-5">
                <p className="text-xs font-semibold text-zinc-400 mb-2.5 uppercase tracking-wider">Risk Breakdown</p>
                <ul className="space-y-2">
                  {simResult.riskFactors.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                      <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${rc.text}`} />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Strategic advice */}
              <div className="mb-5">
                <p className="text-xs font-semibold text-zinc-400 mb-2.5 uppercase tracking-wider">Strategic Recommendation</p>
                <p className="text-sm text-zinc-300 leading-relaxed">{simResult.advice}</p>
              </div>

              {/* Counter-narrative generator */}
              <div className={`p-4 rounded-xl border mb-5 ${rc.bg} ${rc.border}`}>
                <div className="flex items-center justify-between mb-2.5">
                  <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Counter-Narrative Generator</p>
                  <button onClick={handleCopy}
                    className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-zinc-200 transition-colors">
                    {copied
                      ? <><CheckCircle className="w-3.5 h-3.5 text-emerald-400" /> Copied!</>
                      : <><Copy className="w-3.5 h-3.5" /> Copy</>}
                  </button>
                </div>
                <p className="text-sm text-zinc-200 italic leading-relaxed">{simResult.counterNarrative}</p>
                <p className="text-xs text-zinc-400 mt-2">
                  Suggested publish timing: {simResult.peakT === 'T+12h' ? '2h before peak' : '4h before peak'} for maximum pre-bunk effect
                </p>
              </div>

              {/* Export actions */}
              <div className="flex gap-3 flex-wrap">
                <button onClick={handleExportJSON}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-medium bg-zinc-800/60 hover:bg-zinc-700/60 border border-zinc-700/50 text-zinc-200 transition-colors">
                  <FileText className="w-4 h-4 text-indigo-400" /> Export Client Crisis Brief
                </button>
                <button className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-medium bg-zinc-800/60 hover:bg-zinc-700/60 border border-zinc-700/50 text-zinc-200 transition-colors">
                  <Send className="w-4 h-4 text-cyan-400" /> Push to Active Workspace
                </button>
                <button className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-medium bg-zinc-800/60 hover:bg-zinc-700/60 border border-zinc-700/50 text-zinc-200 transition-colors">
                  <Network className="w-4 h-4 text-violet-400" /> Add to Narrative Mapping
                </button>
                <button onClick={handleExportJSON}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-medium bg-zinc-800/60 hover:bg-zinc-700/60 border border-zinc-700/50 text-zinc-200 transition-colors">
                  <Download className="w-4 h-4 text-emerald-400" /> Download JSON
                </button>
              </div>
            </div>

          </div>
        );
      })()}

      {/* EMPTY STATE */}
      {!simResult && !isSimulating && !batchResults && (
        <div className="bg-zinc-900/20 border border-zinc-800/30 rounded-2xl p-12 flex items-center justify-center">
          <p className="text-zinc-400 text-center text-sm">
            Enter a narrative or headline above and click <strong className="text-zinc-400">Run Full Simulation</strong> to see the projected 48h battlefield.
          </p>
        </div>
      )}

      {/* SIMULATION HISTORY */}
      {simHistory.length > 0 && (
        <div className="bg-zinc-900/30 border border-zinc-800/40 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-4 h-4 text-zinc-400" />
            <h4 className="text-sm font-bold text-zinc-300 uppercase tracking-wide">Run History (last {simHistory.length})</h4>
            <button onClick={() => setSimHistory([])} className="ml-auto text-xs text-zinc-500 hover:text-zinc-300 transition-colors">Clear</button>
          </div>
          <div className="space-y-2">
            {simHistory.map((h, i) => {
              const hc = riskColor(h.label);
              return (
                <div key={i} className="flex items-center gap-3 bg-zinc-800/30 rounded-xl px-4 py-2.5">
                  <span className="text-xs text-zinc-500 w-14 flex-shrink-0 font-mono">{h.ts}</span>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-lg flex-shrink-0 ${hc.bg} ${hc.text} ${hc.border} border`}>{h.label}</span>
                  <span className="text-zinc-400 text-xs flex-1 truncate">{h.text}…</span>
                  <div className="flex items-center gap-2 shrink-0">
                    <div className="w-16 bg-zinc-700/50 rounded-full h-1.5">
                      <div className={`h-1.5 rounded-full ${h.score > 70 ? 'bg-rose-500' : h.score > 45 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${h.score}%` }} />
                    </div>
                    <span className={`text-xs font-mono font-bold ${h.score > 70 ? 'text-rose-400' : h.score > 45 ? 'text-amber-400' : 'text-emerald-400'}`}>{h.score}</span>
                    <span className="text-xs text-zinc-500">pk:{fmtK(h.peak)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

    </div>
  );
};

export default CampaignSimulation;
